import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

import ArrowIcon from './drop-down-mini.svg';
import CheckIcon from './check.svg';

interface Option {
  value: string;
  label: string;
}

interface CustomSelectProps {
  options: Option[];
  updateFunc: (newValue: string) => void;
}

const CustomSelectContainer = styled.div`
  position: relative;
  display: inline-block;
  margin-left: 10px;
`;

const SelectDropdown = styled.ul`
  list-style: none;
  margin: 0;
  position: absolute;
  top: 115%;
  left: 0;
  width: 100%;
  background-color: #fff;
  border-radius: 20px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
  z-index: 1;
  overflow: hidden;
`;

const SelectOption = styled.li(({ theme }) => {
  return `
    position: relative;
    padding: 12px;
    cursor: pointer;
  
    &:hover {
      background-color: #f5f5f5;
    }
  
    &.selected {
      color: ${theme.new.color.primary[1]};
    }
  `;
});

const SelectButton = styled.button(({ theme }) => {
  return `
    position: relative;
    padding: 8px 8px 8px 10px;
    font-size: ${theme.new.type.size[16]};
    border: 1px solid ${theme.new.color.neutral[6]};
    background-color: ${theme.new.color.neutral[0]};
    cursor: pointer;
    border-radius: 1000px;  
    text-align: left;
  `;
});

const Check = styled.img`
  width: 16px;
  height: 16px;
  margin-left: 20px;
  display: inline-block;
  position: relative;
  top: 16px;
  right: 16px;
  position: absolute;
`;

const OptionText = styled.span(({ theme }) => {
  return `
        font-size: ${theme.new.type.size[16]};
    `;
});

const Arrow = styled.img`
  width: 24px;
  height: 24px;
  margin-left: 20px;
  display: inline-block;
  position: absolute;
  top: 7px;
  right: 10px;
`;

const StyledSelect: React.FC<CustomSelectProps> = ({ options, updateFunc }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [longestOptionLength, setLongestOptionLength] = useState(100);
  const [selectedOption, setSelectedOption] = useState<Option>(options[0]);
  const selectRef = useRef<HTMLDivElement>(null);

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleSelectOption = (option: Option) => {
    setSelectedOption(option);
    setIsOpen(false);
    updateFunc(option.value);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    let maxLength = 0;
    for (const option of options) {
      if (option.label.length > maxLength) {
        maxLength = option.label.length;
      }
    }

    setLongestOptionLength(maxLength);
  }, [options]);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <CustomSelectContainer ref={selectRef}>
      <SelectButton style={{ width: `${longestOptionLength * 10 + 70}px` }} onClick={handleToggleDropdown}>
        {selectedOption.label}
        <Arrow src={ArrowIcon} />
      </SelectButton>
      {isOpen && (
        <SelectDropdown>
          {options.map((option) => {
            const selected = option.value === selectedOption?.value;
            return (
              <SelectOption
                className={selected ? 'selected' : ''}
                key={option.value}
                onClick={() => handleSelectOption(option)}
              >
                <OptionText>{option.label}</OptionText>

                {selected && <Check src={CheckIcon}></Check>}
              </SelectOption>
            );
          })}
        </SelectDropdown>
      )}
    </CustomSelectContainer>
  );
};

export default StyledSelect;
